import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

// Bullet Chart Component - Designed specifically for performance vs benchmarks
export const BulletChart: React.FC<ChartProps> = ({ metrics, chartId = 'bullet' }) => {
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPerformanceColors = (brandValue: number, industryMean: number) => {
    const absoluteDifference = calculateAbsoluteDifference(brandValue, industryMean);
    const perfLevel = getPerformanceLevel(absoluteDifference);

    const colorMaps = {
      'excellent': { color: '#16a34a', bg: '#dcfce7', performance: 'excellent' as const },
      'very-good': { color: '#22c55e', bg: '#ecfccb', performance: 'very-good' as const },
      'good': { color: '#4ade80', bg: '#f0fdf4', performance: 'good' as const },
      'fair': { color: '#f59e0b', bg: '#fef3c7', performance: 'fair' as const },
      'poor': { color: '#f97316', bg: '#fed7aa', performance: 'poor' as const },
      'critical': { color: '#dc2626', bg: '#fecaca', performance: 'critical' as const }
    };

    return colorMaps[perfLevel.performance];
  };

  useEffect(() => {
    if (!chartRef.current || !containerRef.current || metrics.length === 0) return;

    const container = containerRef.current;
    const svg = d3.select(chartRef.current);
    svg.selectAll('*').remove();

    const containerRect = container.getBoundingClientRect();
    const margin = { top: 20, right: 60, bottom: 20, left: 200 };
    const width = containerRect.width - margin.left - margin.right;
    const bulletHeight = 50;
    const height = metrics.length * bulletHeight;

    svg.attr('width', containerRect.width).attr('height', height + margin.top + margin.bottom);

    // Create tooltip
    const tooltip = d3.select('body').selectAll(`.bullet-tooltip-${chartId}`)
      .data([null])
      .join('div')
      .attr('class', `bullet-tooltip bullet-tooltip-${chartId}`)
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '1000')
      .style('pointer-events', 'none');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Filter metrics with industry data
    const validMetrics = metrics.filter(d => d.industryMean && d.industryMean > 0);

    if (validMetrics.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#6b7280')
        .text('No industry benchmark data available');
      return;
    }

    // Calculate scales
    const maxValue = d3.max(validMetrics, d => Math.max(d.value, d.industryMean || 0)) || 100;
    const xScale = d3.scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([0, width]);

    // Create bullet chart for each metric
    validMetrics.forEach((d, i) => {
      const yPos = i * bulletHeight;
      const colors = getPerformanceColors(d.value, d.industryMean || 0);
      const absoluteDiff = calculateAbsoluteDifference(d.value, d.industryMean || 0);

      // Add invisible hover area for tooltip (drawn first, so it's at the bottom)
      const rowGroup = g.append('g');

      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', yPos + 5)
        .attr('width', width)
        .attr('height', 30)
        .attr('fill', 'transparent')
        .attr('pointer-events', 'all')
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          const performanceText = colors.performance === 'excellent' ? 'Outstanding Performance' :
                                 colors.performance === 'very-good' ? 'Strong Performance' :
                                 colors.performance === 'good' ? 'Above Average' :
                                 colors.performance === 'fair' ? 'Below Average' :
                                 colors.performance === 'poor' ? 'Needs Improvement' : 'Urgent Attention Required';

          tooltip.html(`
            <div style="
              background: ${colors.bg};
              border: 2px solid ${colors.color};
              border-radius: 12px;
              padding: 16px;
              min-width: 200px;
              box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.25);
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            ">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px;">
                ${d.categoryName}
              </div>
              <div style="color: #475569; margin-bottom: 8px; font-size: 12px;">
                ${performanceText}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Brand Score:</span>
                <span style="font-weight: 600; color: #1e293b; font-size: 12px;">${d.value.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #64748b; font-size: 12px;">Industry Avg:</span>
                <span style="font-weight: 600; color: #64748b; font-size: 12px;">${d.industryMean?.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #64748b; font-size: 12px;">Difference:</span>
                <span style="font-weight: 600; color: ${absoluteDiff >= 0 ? colors.color : '#dc2626'}; font-size: 12px;">
                  ${absoluteDiff >= 0 ? '+' : ''}${absoluteDiff.toFixed(1)}%
                </span>
              </div>
            </div>
          `)
          .style('visibility', 'visible');
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
        });

      // Performance ranges (background bars)
      const ranges = [
        { value: (d.industryMean || 0) * 0.7, color: '#fca5a5', label: 'Poor' },      // 70% of industry
        { value: (d.industryMean || 0) * 0.9, color: '#fed7aa', label: 'Fair' },      // 90% of industry
        { value: (d.industryMean || 0) * 1.1, color: '#fef3c7', label: 'Good' },      // 110% of industry
        { value: (d.industryMean || 0) * 1.3, color: '#ecfccb', label: 'Excellent' }, // 130% of industry
      ];

      // Draw performance range backgrounds
      ranges.forEach((range, idx) => {
        rowGroup.append('rect')
          .attr('x', 0)
          .attr('y', yPos + 10)
          .attr('width', xScale(range.value))
          .attr('height', 20)
          .attr('fill', range.color)
          .attr('opacity', 0.3)
          .attr('pointer-events', 'none');
      });

      // Draw industry average line (benchmark marker)
      rowGroup.append('line')
        .attr('x1', xScale(d.industryMean || 0))
        .attr('x2', xScale(d.industryMean || 0))
        .attr('y1', yPos + 5)
        .attr('y2', yPos + 35)
        .attr('stroke', '#94a3b8')
        .attr('stroke-width', 3)
        .attr('pointer-events', 'none');

      // Draw brand performance bar
      const performanceBar = rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', yPos + 12)
        .attr('width', xScale(d.value))
        .attr('height', 16)
        .attr('fill', colors.color)
        .attr('rx', 2)
        .attr('pointer-events', 'none');

      // Add hover effect to highlight the bar
      rowGroup.select('rect[fill="transparent"]')
        .on('mouseover.bar', function() {
          performanceBar.attr('opacity', 0.8);
        })
        .on('mouseout.bar', function() {
          performanceBar.attr('opacity', 1);
        });

      // Add metric label
      const categoryLabel = g.append('text')
        .attr('x', -10)
        .attr('y', yPos + 22)
        .attr('text-anchor', 'end')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .attr('fill', '#374151')
        .text(d.categoryName.length > 25 ? d.categoryName.substring(0, 25) + '...' : d.categoryName);

      // Add tooltip for truncated labels
      if (d.categoryName.length > 25) {
        categoryLabel.append('title').text(d.categoryName);
      }

      // Add value label - ensure minimum distance from left to avoid overlap
      const valuePosition = xScale(d.value);
      const minValuePosition = 10; // Minimum 10px from left edge
      const finalValuePosition = Math.max(valuePosition + 5, minValuePosition);

      g.append('text')
        .attr('x', finalValuePosition)
        .attr('y', yPos + 22)
        .attr('text-anchor', 'start')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', colors.color)
        .text(`${d.value.toFixed(1)}%`);
    });

    // Cleanup function
    return () => {
      d3.select('body').select(`.bullet-tooltip-${chartId}`).remove();
    };
  }, [metrics, chartId]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={chartRef}></svg>
    </div>
  );
};